import { saveSchedule } from "../../../../services/branchSchedule.service.ts";
import { getDateByDayOfWeek } from "../../../../utils/constanst.js";
import {
  normalizePositionIdKey,
  dedupePartnerDayShiftPositionItems,
  attachMissingOriginalRecordIds,
  dedupeScheduleRecordsForSave,
  WeeklySaveCellContext,
} from "./scheduleKeyUtils.ts";

function normalizeShiftNameForSave(shift: any): string {
  if (shift == null || shift === "") return "";
  if (typeof shift === "string") return shift.trim();
  if (typeof shift === "object" && (shift as { name?: string }).name != null) {
    return String((shift as { name: string }).name).trim();
  }
  return String(shift).trim();
}

function normalizePositionLabelForSave(value: string): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export type PersistWeeklyBranchScheduleParams = {
  branchId: number;
  weekValue: string;
  items: any[];
  weeklyCtx: WeeklySaveCellContext;
  removedScheduleIds: Set<number>;
  branchHours: any | null;
  schedulesForSave: any[];
};

/** Persiste la grilla semanal únicamente en BranchSchedule (check-schedule). */
export async function persistWeeklyBranchSchedule(
  params: PersistWeeklyBranchScheduleParams,
): Promise<void> {
  const {
    branchId,
    weekValue,
    items,
    weeklyCtx,
    removedScheduleIds,
    branchHours,
    schedulesForSave,
  } = params;

  const shiftToNum: Record<string, number> = {
    Matutino: 0,
    Intermedio: 1,
    Vespertino: 2,
  };
  const shiftNames = ["Matutino", "Intermedio", "Vespertino"];
  const formatTime = (t: string) =>
    String(t ?? "").trim().length >= 5
      ? String(t).trim().slice(0, 5)
      : String(t ?? "").trim();

  const getStartEndForShift = (shiftName: string) => {
    if (!branchHours) return { startTime: "", endTime: "" };
    if (shiftName === "Matutino")
      return {
        startTime: formatTime(branchHours.matutinoStart),
        endTime: formatTime(branchHours.matutinoEnd),
      };
    if (shiftName === "Intermedio")
      return {
        startTime: formatTime(branchHours.intermedioStart),
        endTime: formatTime(branchHours.intermedioEnd),
      };
    if (shiftName === "Vespertino")
      return {
        startTime: formatTime(branchHours.vespertinoStart),
        endTime: formatTime(branchHours.vespertinoEnd),
      };
    return { startTime: "", endTime: "" };
  };

  const timeToMinutes = (s: string) => {
    const t = String(s ?? "").trim().slice(0, 5);
    const [h, m] = t.split(":").map(Number);
    return (h || 0) * 60 + (m || 0);
  };
  const parseStartEnd = (rangeStr: string) => {
    if (String(rangeStr || "").includes(" - ")) {
      const parts = rangeStr.trim().split(" - ");
      return {
        start: timeToMinutes(parts[0]),
        end: timeToMinutes(parts[1] ?? parts[0]),
      };
    }
    return { start: 0, end: 0 };
  };
  const getPositionXW = (
    startEndStr: string,
    shiftStart: string,
    shiftEnd: string,
  ) => {
    if (!branchHours) return { positionX: 0, positionW: 1 };
    const gridStartMin = timeToMinutes(branchHours.matutinoStart);
    const { start: startMin, end: endMin } = parseStartEnd(startEndStr);
    const start = startMin || timeToMinutes(shiftStart);
    const end = endMin || timeToMinutes(shiftEnd);
    if (end <= start) return { positionX: 0, positionW: 1 };
    const positionX = Math.max(0, Math.floor((start - gridStartMin) / 30));
    const positionW = Math.max(1, Math.floor((end - start) / 30));
    return { positionX, positionW };
  };

  const positionItems = dedupePartnerDayShiftPositionItems(
    items.filter((it: any) => it.positionId != null && it.positionId !== ""),
  ).map((it: any) => {
    const bid = Number(it.branchScheduleId);
    if (!Number.isNaN(bid) && bid > 0) {
      const found = schedulesForSave.find((s: any) => Number(s.id) === bid);
      if (found) {
        return {
          ...it,
          shift: normalizeShiftNameForSave(found.shift),
        };
      }
    }
    return {
      ...it,
      shift: normalizeShiftNameForSave(it.shift) || "Matutino",
    };
  });
  const byDayShift: Record<string, any[]> = {};
  positionItems.forEach((it: any) => {
    const key = `${it.day}|${it.shift}`;
    if (!byDayShift[key]) byDayShift[key] = [];
    byDayShift[key].push(it);
  });

  const existingByDayShift: Record<string, any[]> = {};
  schedulesForSave.forEach((s: any) => {
    if (removedScheduleIds.has(Number(s?.id))) return;
    const day = s.dayOfWeek;
    const sn = normalizeShiftNameForSave(s.shift);
    if (!day || !sn) return;
    const key = `${day}|${sn}`;
    if (!existingByDayShift[key]) existingByDayShift[key] = [];
    existingByDayShift[key].push(s);
  });

  const weekDaysList = [
    "Lunes",
    "Martes",
    "Miercoles",
    "Jueves",
    "Viernes",
    "Sabado",
    "Domingo",
  ];

  const saveTasks: Promise<unknown>[] = [];

  for (const day of weekDaysList) {
    for (const shiftName of shiftNames) {
      const shiftNum = shiftToNum[shiftName];
      const desiredItems = byDayShift[`${day}|${shiftName}`] || [];
      const existing = existingByDayShift[`${day}|${shiftName}`] || [];
      const existingByPartnerPositionGroups: Record<string, any[]> = {};
      existing.forEach((s: any) => {
        const pid = s.partner?.id ?? s.partner;
        const posId = s.position?.id ?? s.position;
        if (pid != null && posId != null) {
          const gkey = `${Number(pid)}_${normalizePositionIdKey(posId)}`;
          if (!existingByPartnerPositionGroups[gkey]) {
            existingByPartnerPositionGroups[gkey] = [];
          }
          existingByPartnerPositionGroups[gkey].push(s);
        }
      });
      const findExistingForDesiredItem = (it: any) => {
        const gkey = `${Number(it.partnerId)}_${normalizePositionIdKey(it.positionId)}`;
        const candidates = existingByPartnerPositionGroups[gkey] || [];
        const want = normalizePositionLabelForSave(
          it.positionDisplayName || it.positionName || "",
        );
        if (candidates.length === 0) {
          const byId = existing.find(
            (s: any) =>
              Number(s.partner?.id ?? s.partner) === Number(it.partnerId) &&
              Number(s.position?.id ?? s.position) === Number(it.positionId),
          );
          if (byId) return byId;
          if (!want) return undefined;
          return existing.find(
            (s: any) =>
              Number(s.partner?.id ?? s.partner) === Number(it.partnerId) &&
              normalizePositionLabelForSave(
                s.positionName || s.position?.name || "",
              ) === want,
          );
        }
        if (candidates.length === 1) return candidates[0];
        const byName = candidates.find(
          (s: any) =>
            normalizePositionLabelForSave(s.positionName || "") === want,
        );
        return byName || candidates[0];
      };

      const { startTime: startRaw, endTime: endRaw } =
        getStartEndForShift(shiftName);
      const exactDateObj = getDateByDayOfWeek(weekValue, day);
      const exactDateStr = exactDateObj
        ? `${exactDateObj.getFullYear()}-${String(exactDateObj.getMonth() + 1).padStart(2, "0")}-${String(exactDateObj.getDate()).padStart(2, "0")}`
        : "";
      const timeRangeStr = startRaw && endRaw ? `${startRaw} - ${endRaw}` : "";

      const formatPartTime = (t: string) =>
        String(t ?? "").trim().length >= 5
          ? String(t).trim().slice(0, 5)
          : String(t ?? "").trim();
      const firstOfRange = (r: string) => {
        const str = String(r ?? "").trim();
        if (!str) return "";
        if (str.includes(" - "))
          return formatPartTime(str.split(" - ")[0]?.trim() || str);
        return formatPartTime(str);
      };
      const lastOfRange = (r: string) => {
        const str = String(r ?? "").trim();
        if (!str) return "";
        if (str.includes(" - ")) {
          const parts = str.split(" - ").map((x: string) => x.trim());
          return formatPartTime(parts[parts.length - 1] || parts[0] || str);
        }
        return formatPartTime(str);
      };
      const toTimeRange = (s: string, e: string) => {
        if (!s || !e) return s && e ? "" : s || e || "";
        const inicio = firstOfRange(s);
        const fin = lastOfRange(e);
        if (!inicio || !fin) return inicio && fin ? "" : inicio || fin || "";
        return `${inicio} - ${fin}`;
      };

      const scheduleRecordsFromDesired: any[] = desiredItems.map((it: any) => {
        const existingRecord = (() => {
          if (it.branchScheduleId != null && it.branchScheduleId !== undefined) {
            const bid = Number(it.branchScheduleId);
            const inBatch = existing.find((s: any) => Number(s.id) === bid);
            if (inBatch) return inBatch;
            const found = schedulesForSave.find(
              (s: any) => Number(s.id) === bid,
            );
            if (found) return found;
          }
          return findExistingForDesiredItem(it);
        })();
        const startTime = existingRecord
          ? toTimeRange(
              existingRecord.startTime ?? startRaw,
              existingRecord.endTime ?? endRaw,
            ) || timeRangeStr
          : timeRangeStr;
        const endTime = existingRecord
          ? toTimeRange(
              existingRecord.startTime ?? startRaw,
              existingRecord.endTime ?? endRaw,
            ) || timeRangeStr
          : timeRangeStr;
        const { positionX: posX, positionW: posW } = getPositionXW(
          startTime || timeRangeStr,
          startRaw,
          endRaw,
        );
        const positionNameForGrid =
          it.positionDisplayName || it.positionName || "";
        if (existingRecord) {
          return {
            dateRange: weekValue,
            dayOfWeek: day,
            positionName: positionNameForGrid,
            positionX: existingRecord.positionX ?? posX,
            positionW: existingRecord.positionW ?? posW,
            positionY: existingRecord.positionY ?? 0,
            startTime,
            endTime,
            exactDate: exactDateStr,
            branch: branchId,
            partner: it.partnerId,
            position: it.positionId,
            shift:
              normalizeShiftNameForSave(existingRecord.shift) || shiftName,
            positionOriginalId_:
              existingRecord.positionOriginalId ?? it.positionId,
            originalRecordId: existingRecord.id,
          };
        }
        return {
          dateRange: weekValue,
          dayOfWeek: day,
          positionName: positionNameForGrid,
          positionX: posX,
          positionW: posW,
          positionY: 0,
          startTime: timeRangeStr,
          endTime: timeRangeStr,
          exactDate: exactDateStr,
          branch: branchId,
          partner: it.partnerId,
          position: it.positionId,
          shift: shiftName,
          positionOriginalId_: it.positionId,
        };
      });

      const linkedDesiredRecords = attachMissingOriginalRecordIds(
        scheduleRecordsFromDesired,
        existing,
      );

      const scheduleRecords = dedupeScheduleRecordsForSave(linkedDesiredRecords);

      if (!exactDateStr) continue;
      if (scheduleRecords.length === 0 && existing.length === 0) continue;
      saveTasks.push(
        saveSchedule({
          branchId,
          schedule: scheduleRecords,
          exactDate: exactDateStr,
          shift: shiftNum,
          allowDuplicatePartnerPosition: false,
        }),
      );
    }
  }

  await Promise.all(saveTasks);
}
